import { useState } from 'react';

import {
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';
import { isValidEmailFormat } from '@shared';
import type { Tag } from '../store/store';

export const MAX_BULK_INVITE_CLIENTS = 35;

export function emptyBulkClient(): Tag {
  return {
    value: '',
    isValid: false,
    error: '',
    phoneCountryCode: '+1',
    phoneNumber: '',
  };
}

export function bulkClientsForEmailOnlyMode(clients: Tag[]): Tag[] {
  // Structured MFA bulk entry needs a blank starter row. The email-only tag input does not:
  // rendering that row there produces an empty destructive badge. Preserve every row where the
  // advisor entered an email and remove only the structured editor's blank placeholders.
  return clients.filter(client => client.value.trim().length > 0);
}

function parsePastedPhone(rawPhone: string): {
  countryCode: SupportedCountryCode;
  digits: string;
} {
  const digits = stripDigits(rawPhone);
  if (rawPhone.trim().startsWith('+91') && digits.startsWith('91')) {
    return { countryCode: '+91', digits: digits.slice(2) };
  }
  if (rawPhone.trim().startsWith('+63') && digits.startsWith('63')) {
    return { countryCode: '+63', digits: digits.slice(2) };
  }
  if (rawPhone.trim().startsWith('+1') && digits.length === 11 && digits.startsWith('1')) {
    return { countryCode: '+1', digits: digits.slice(1) };
  }
  return { countryCode: '+1', digits };
}

function parsePastedClients(pasteText: string, available: number): Tag[] {
  return pasteText
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .slice(0, available)
    .map(line => {
      const [email = '', rawPhone = ''] = line.split(',').map(value => value.trim());
      const { countryCode, digits } = parsePastedPhone(rawPhone);
      return {
        value: email,
        isValid: isValidEmailFormat(email),
        error: isValidEmailFormat(email) ? '' : 'Please enter a valid email address',
        phoneCountryCode: countryCode,
        phoneNumber: formatByCountry(digits, countryCode),
      } satisfies Tag;
    });
}

export function countPastedClients(pasteText: string, available: number): number {
  return parsePastedClients(pasteText, available).length;
}

export function useBulkMobileMfaClients({
  clients,
  onChange,
  onClear,
}: {
  clients: Tag[];
  onChange: (clients: Tag[]) => void;
  onClear: () => void;
}) {
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteReview, setPasteReview] = useState<{ added: number; invalid: number } | null>(null);
  const atLimit = clients.length >= MAX_BULK_INVITE_CLIENTS;
  const populatedClientCount = clients.filter(client => client.value || client.phoneNumber).length;
  const pasteClientCount = countPastedClients(
    pasteText,
    Math.max(0, MAX_BULK_INVITE_CLIENTS - populatedClientCount),
  );

  const updateClient = (index: number, changes: Partial<Tag>) => {
    onChange(
      clients.map((client, clientIndex) =>
        clientIndex === index ? { ...client, ...changes } : client,
      ),
    );
  };

  const updateEmail = (index: number, value: string) => {
    updateClient(index, {
      value,
      isValid: isValidEmailFormat(value),
      error: value && !isValidEmailFormat(value) ? 'Please enter a valid email address' : '',
    });
  };

  const updateCountryCode = (
    index: number,
    countryCode: SupportedCountryCode,
    phoneNumber: string,
  ) => {
    updateClient(index, {
      phoneCountryCode: countryCode,
      phoneNumber: formatByCountry(stripDigits(phoneNumber), countryCode),
    });
  };

  const updatePhoneNumber = (
    index: number,
    phoneNumber: string,
    countryCode: SupportedCountryCode,
  ) => {
    updateClient(index, {
      phoneNumber: formatByCountry(stripDigits(phoneNumber), countryCode),
    });
  };

  const removeClient = (index: number) => {
    onChange(clients.filter((_, clientIndex) => clientIndex !== index));
  };

  const addClient = () => {
    if (!atLimit) onChange([...clients, emptyBulkClient()]);
  };

  const addPastedClients = () => {
    const existing = clients.filter(client => client.value || client.phoneNumber);
    const available = Math.max(0, MAX_BULK_INVITE_CLIENTS - existing.length);
    const parsed = parsePastedClients(pasteText, available);
    const next = [...existing, ...parsed].slice(0, MAX_BULK_INVITE_CLIENTS);
    const invalid = parsed.filter(
      client =>
        !client.isValid ||
        validateForCountry(
          stripDigits(client.phoneNumber ?? ''),
          client.phoneCountryCode ?? '+1',
        ) !== null,
    ).length;

    onChange(next.length > 0 ? next : [emptyBulkClient()]);
    setPasteReview({ added: parsed.length, invalid });
    setPasteText('');
    setIsPasteOpen(false);
  };

  const clearClients = () => {
    onClear();
    setPasteReview(null);
    setPasteText('');
    setIsPasteOpen(false);
  };

  return {
    isPasteOpen,
    openPaste: () => setIsPasteOpen(true),
    closePaste: () => setIsPasteOpen(false),
    pasteText,
    setPasteText,
    pasteClientCount,
    pasteReview,
    atLimit,
    updateEmail,
    updateCountryCode,
    updatePhoneNumber,
    removeClient,
    addClient,
    addPastedClients,
    clearClients,
  };
}
